/**
 * Shared prepaid-wallet summary — ONE request feeds the sidebar item, the
 * low-balance banner and the wallet page (module-level cache + subscribers),
 * so mounting all three costs a single GET.
 *
 * Background-safe: the layout's prefetch passes suppressPermissionLock, and any
 * failure (403 for a role without billing access, network) just reads as
 * "no wallet" — the nav never breaks because of it.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import walletApi, { type WalletSummary } from '@/services/wallet.api';

const STALE_MS = 60_000;

interface State {
  summary: WalletSummary | null;
  loaded: boolean;
  error: boolean;
  fetchedAt: number;
}

let state: State = { summary: null, loaded: false, error: false, fetchedAt: 0 };
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const getSnapshot = () => state;

/** Fetch now (coalesced). `force` skips the staleness window. */
export function refreshWalletSummary(force = false): Promise<void> {
  if (!force && state.loaded && Date.now() - state.fetchedAt < STALE_MS) return Promise.resolve();
  if (inflight) return inflight;
  inflight = walletApi
    .getSummary({ suppressPermissionLock: true })
    .then((summary) => {
      state = { summary, loaded: true, error: false, fetchedAt: Date.now() };
    })
    .catch(() => {
      state = { ...state, loaded: true, error: true, fetchedAt: Date.now() };
    })
    .finally(() => {
      inflight = null;
      emit();
    });
  return inflight;
}

/** Push a known-fresh summary (e.g. right after a credited top-up). */
export function setWalletSummary(summary: WalletSummary): void {
  state = { summary, loaded: true, error: false, fetchedAt: Date.now() };
  emit();
}

export function useWalletSummary(opts: { enabled?: boolean } = {}) {
  const enabled = opts.enabled ?? true;
  const snap = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    if (!enabled) return undefined;
    void refreshWalletSummary();
    // Balance moves with usage: re-check (within the staleness window) when the tab regains focus.
    const onFocus = () => void refreshWalletSummary();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled]);

  const refresh = useCallback(() => refreshWalletSummary(true), []);

  return {
    summary: snap.summary,
    loaded: snap.loaded,
    error: snap.error,
    /** Wallet switched on by the platform for this workspace. */
    walletEnabled: Boolean(snap.summary?.enabled),
    refresh,
  };
}
